<google-map height="100%" width="100%" [center]="center" [zoom]="zoom" [options]="mapOptions">
  <!-- Vehículos de la flota (semáforo) -->
  <map-marker
    *ngFor="let v of vehicles; trackBy: trackById"
    #marker="mapMarker"
    [position]="position(v)"
    [title]="(v.plate || '') + ' · ' + v.telemetry.speedKmh + ' km/h'"
    [icon]="iconFor(v)"
    (mapClick)="onMarkerClick(marker, v)"
  ></map-marker>

  <!-- Talleres (capa de service centers) -->
  <map-marker
    *ngFor="let w of visibleWorkshops; trackBy: trackByWorkshop"
    #wmarker="mapMarker"
    [position]="workshopPosition(w)"
    [title]="w.name"
    [icon]="workshopIcon"
    (mapClick)="onWorkshopClick(wmarker, w)"
  ></map-marker>

  <map-info-window [options]="infoOptions">
    <!-- Vehículo -->
    <app-vehicle-mini-card *ngIf="infoMode === 'vehicle'" [vehicle]="activeVehicle"></app-vehicle-mini-card>

    <!-- Taller -->
    <div *ngIf="infoMode === 'workshop' && activeWorkshop" class="w-[240px] max-w-[240px] overflow-hidden">
      <img
        *ngIf="activeWorkshop.image && !imgError"
        [src]="activeWorkshop.image"
        alt="{{ activeWorkshop.name }}"
        width="240"
        height="112"
        class="mb-2 block h-28 w-[240px] max-w-[240px] rounded-lg object-cover"
        referrerpolicy="no-referrer"
        (error)="imgError = true"
      />
      <div class="mb-1 flex items-center gap-2">
        <span class="grid h-6 w-6 place-content-center rounded-full bg-orange-500 text-[13px] text-white">🔧</span>
        <span class="text-sm font-bold leading-tight text-[#0e1726]">{{ activeWorkshop.name }}</span>
      </div>
      <div *ngIf="activeWorkshop.categories.length" class="mt-2 flex flex-wrap gap-1">
        <span
          *ngFor="let c of activeWorkshop.categories"
          class="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-[11px] font-medium text-orange-700"
        >
          {{ categoryEmoji(c) }} {{ c }}
        </span>
      </div>
      <div *ngIf="!activeWorkshop.categories.length" class="mt-1 text-[11px] text-gray-400">Sin servicios registrados</div>
    </div>
  </map-info-window>
</google-map>

<!-- Panel de control de la capa de talleres (flotante) -->
<div
  class="absolute bottom-4 right-4 z-20 w-64 rounded-2xl border border-gray-100 bg-white/95 p-3 shadow-xl backdrop-blur dark:border-white/10 dark:bg-[#0e1726]/95"
>
  <div class="mb-2 flex items-center justify-between">
    <div class="flex items-center gap-2">
      <span class="grid h-6 w-6 place-content-center rounded-full bg-orange-500 text-[13px] text-white">🔧</span>
      <span class="text-sm font-bold text-[#0e1726] dark:text-white">Talleres</span>
    </div>
    <!-- Toggle -->
    <button
      type="button"
      role="switch"
      [attr.aria-checked]="showWorkshops"
      (click)="toggleWorkshops()"
      class="relative h-6 w-11 rounded-full transition-colors"
      [ngClass]="showWorkshops ? 'bg-orange-500' : 'bg-gray-300 dark:bg-white/20'"
    >
      <span class="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all" [ngClass]="showWorkshops ? 'left-[22px]' : 'left-0.5'"></span>
    </button>
  </div>

  <div class="mb-2 text-[11px] text-gray-500 dark:text-white/60">
    <ng-container *ngIf="loadingWorkshops">Cargando talleres…</ng-container>
    <ng-container *ngIf="!loadingWorkshops">
      Mostrando <b class="text-orange-500 dark:text-orange-400">{{ visibleWorkshops.length }}</b> de {{ workshops.length }} talleres
    </ng-container>
  </div>

  <!-- Filtro por categoría -->
  <div *ngIf="!loadingWorkshops && showWorkshops" class="space-y-1.5">
    <div class="flex items-center justify-between">
      <span class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Categorías</span>
      <button *ngIf="activeCategories.size" (click)="clearCategories()" class="text-[10px] font-semibold text-orange-500 hover:underline">Todas</button>
    </div>
    <div class="flex max-h-40 flex-col gap-1 overflow-y-auto pr-1">
      <button
        *ngFor="let c of categories"
        type="button"
        (click)="toggleCategory(c.name)"
        class="flex items-center justify-between rounded-lg border px-2 py-1.5 text-left text-[11px] transition-colors"
        [ngClass]="
          isCategoryActive(c.name)
            ? 'border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-500/40 dark:bg-orange-500/15 dark:text-orange-300'
            : 'border-gray-100 bg-white text-gray-400 dark:border-white/10 dark:bg-white/5'
        "
      >
        <span class="flex items-center gap-1.5 truncate"><span>{{ c.icon }}</span><span class="truncate">{{ c.name }}</span></span>
        <span class="ml-1 shrink-0 rounded-full bg-black/5 px-1.5 py-0.5 text-[10px] font-semibold dark:bg-white/10">{{ c.count }}</span>
      </button>
    </div>
  </div>

  <!-- Leyenda del semáforo de vehículos -->
  <div class="mt-3 border-t border-gray-100 pt-2 dark:border-white/10">
    <span class="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">Vehículos</span>
    <div class="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600 dark:text-white/70">
      <span class="flex items-center gap-1"><span class="h-2.5 w-2.5 rounded-full bg-emerald-500"></span>Movimiento</span>
      <span class="flex items-center gap-1"><span class="h-2.5 w-2.5 rounded-full bg-red-500"></span>Detenido</span>
      <span class="flex items-center gap-1"><span class="h-2.5 w-2.5 rounded-full bg-amber-500"></span>Ralentí</span>
    </div>
  </div>
</div>
